import { useState, useRef, useEffect } from 'react';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';

import ruLocale from 'date-fns/locale/ru';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { handleHttpErrorStatus } from '../../../../utils/handleHttpErrorStatus';
import { labelStyles } from '../../../../common/styles/formStyles';
import formatISODate from '../../utils/formatISODate';
import {hetch} from "../../../../utils/fetch.hooked";

const defaultValues = {
  keyName: '',
  fileName: '',
  filePassword: '',
  password: '',
  dateFrom: new Date()
};

function AddCertForm({ defaultKeyName, triggerRefetch, handleClose }) {
  const fileInput = useRef();
  const [isFetching, setIsFetching] = useState(false);
  const [keysList, setKeysList] = useState([]);
  const [formValues, setFormValues] = useState({
    ...defaultValues,
    keyName: defaultKeyName ?? defaultValues.keyName,
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (evt) => {
    const { name, value } = evt.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleFileChange = (evt) => {
    setFormValues({
      ...formValues,
      fileName: evt.target.files[0].name,
    });
  }

  const handleDateChange = (dateFrom) => {
    setFormValues({
      ...formValues,
      dateFrom
    });
  }

  const handleSubmit = (evt) => {
    evt.preventDefault();
    setSubmitted(true);
    setError('');

    if (Object.values([formValues.keyName, formValues.fileName, formValues.password, formValues.dateFrom]).every((value) => value)) {
      const formData = new FormData();
      formData.append('key_file', fileInput.current.files[0], fileInput.current.files[0].name);
      formData.append('keyDtoJson', JSON.stringify({
        key_name: formValues.keyName,
        key_password: formValues.password,
        start_date: formValues.dateFrom ? formatISODate(formValues.dateFrom) : null,
      }));
      formData.append('file_password', formValues.filePassword);

      setIsFetching(true);
      hetch('/api/keys', {
        method: 'POST',
        body: formData,
      })
        .then(handleHttpErrorStatus)
        .then(res => res.json())
        .then(() => {
          handleClose();
          triggerRefetch();
        })
        .catch(err => err.json())
        .then(res => {
          if (res) {
            setError(res.message);
          }
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  };

  useEffect(() => {
    const fetchData = () => {
      hetch('/api/key_names')
        .then(res => res.json())
        .then(res => {
          setKeysList(res?.content ?? []);
        });
    }

    fetchData();
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {error ? (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <InputLabel
            htmlFor='select-name'
            sx={labelStyles}
            required={true}
          >
            Имя УКЭП
          </InputLabel>
          <Select
            id='select-name'
            name='keyName'
            size="small"
            fullWidth
            disabled={defaultKeyName || isFetching}
            error={submitted && !formValues.keyName}
            value={formValues.keyName}
            onChange={handleInputChange}
          >
            {keysList.map(item => (
              <MenuItem
                key={item.id}
                value={item.key_name}
              >
                {item.key_name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12}>
          <InputLabel
            htmlFor='file'
            sx={labelStyles}
            required={true}
          >
            Файл-архив с сертификатом
          </InputLabel>
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <input
              hidden
              type='file'
              accept='.zip'
              ref={fileInput}
              onChange={handleFileChange}
            />
            <TextField
              id='file'
              name='fileName'
              size="small"
              fullWidth
              inputProps={{
                readOnly: true
              }}
              error={submitted && !formValues.fileName}
              value={formValues.fileName}
            />
            <Button
              title="Обзор файлов"
              variant='contained'
              disabled={isFetching}
              sx={{marginLeft: '8px'}}
              onClick={() => fileInput.current.click()}
            >
              Обзор
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <InputLabel
            htmlFor='password'
            sx={labelStyles}
            required={true}
          >
            Пароль от ключа
          </InputLabel>
          <TextField
            id='password'
            name='password'
            type='password'
            disabled={isFetching}
            size="small"
            fullWidth
            error={submitted && !formValues.password}
            value={formValues.password}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel
            htmlFor='password'
            sx={labelStyles}
          >
            Пароль от файл-архива
          </InputLabel>
          <TextField
            id='file-password'
            name='filePassword'
            type='password'
            disabled={isFetching}
            size="small"
            fullWidth
            value={formValues.filePassword}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel
            htmlFor='date-from'
            sx={labelStyles}
            required={true}
          >
            Дата начала действия сертификата
          </InputLabel>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ruLocale}
          >
            <DateTimePicker
              value={formValues.dateFrom}
              onChange={handleDateChange}
              disabled={isFetching}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id='date-from'
                  name='dateFrom'
                  size="small"
                  fullWidth
                  error={submitted && !formValues.dateFrom}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sx={{textAlign: 'center'}}>
          <Button
            title="Сохранить сертификат"
            variant='contained'
            type='submit'
            disabled={isFetching}
          >
            Сохранить
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export default AddCertForm;
